/** @format */

import React, { ReactNode } from 'react';
import { useInstantSearch } from 'react-instantsearch';
import { safeCurrentLanguage } from '@services/drupal-services';
import Translate from '@components/translation/translate-text-wrapper';

/**
 * Check for locale character limit before search begins.
 */
export const characterLimitCheck = (): number => {
  switch (safeCurrentLanguage()) {
    case 'zh-hant':
    case 'zh-hans':
    case 'ja':
      return 1;
    default:
      return 3;
  }
};

/**
 * No Results Boundary.
 */
const NoResultsBoundary: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { results, indexUiState } = useInstantSearch();
  const { __isArtificial: artificial, nbHits, page, hitsPerPage } = results;
  const { query } = indexUiState;
  const endResultCount =
    (page + 1) * hitsPerPage > nbHits
      ? nbHits - page * hitsPerPage
      : (page + 1) * hitsPerPage;
  const noResultsFound =
    (!artificial && nbHits === 0) ||
    (query && query?.length < characterLimitCheck());

  /**
   * Render.
   */
  return (
    <>
      <div className="search-header">
        {/* No query */}
        <div className={`no-query ${!indexUiState.query ? 'show' : 'hide'}`}>
          <h2>
            <Translate>Start typing to search</Translate>.
          </h2>
        </div>
        {/* Search results not found */}
        <div
          className={`no-search-results-found ${
            noResultsFound ? 'show' : 'hide'
          }`}>
          <h2>
            <Translate>No results for </Translate>
            <q>{indexUiState.query}</q>.
          </h2>
          <p>
            <Translate>Your search did not yield any results</Translate>.
          </p>
          <h3>
            <Translate>Suggestions</Translate>:
          </h3>
          <ul>
            <li>
              <Translate>Make sure all words are spelled correctly</Translate>.
            </li>
            <li>
              <Translate>Try different keywords</Translate>.
            </li>
            <li>
              <Translate>Try more general keywords.</Translate>.
            </li>
          </ul>
        </div>

        {/* Search results found */}
        <div
          className={`search-results-wrapper ${
            nbHits > 0 && query && query?.length >= characterLimitCheck()
              ? 'show'
              : 'hide'
          }`}>
          <h2>
            <Translate>Search results for</Translate> <q>{query}</q>
          </h2>
          <p className="result-count">
            <Translate>Showing</Translate> {page * hitsPerPage + 1} -{' '}
            {endResultCount} <Translate>of</Translate> {nbHits}{' '}
            <Translate>results</Translate>
          </p>
          {children}
        </div>
      </div>
    </>
  );
};
export default NoResultsBoundary;
